/**
 * Cuentas y cajas.
 *
 * El saldo **se calcula**, no se guarda. Por eso aquí no hay ningún botón de
 * «ajustar saldo»: si algo no cuadra, es porque falta un movimiento, y lo que hay
 * que hacer es añadirlo. En Cuentia el saldo se guardaba y se desincronizaba sin
 * que nadie se enterara.
 *
 * La tarjeta de crédito se muestra aparte, porque es la más fácil de malinterpretar:
 * debe estar en negativo, y ese negativo es lo que debes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, CreditCard, Landmark, PiggyBank, Plus, Wallet } from 'lucide-react';
import { useState } from 'react';

import { formatCOP, parseAmountFromText } from '@eldescuadre/core';
import type { Account } from '@eldescuadre/contracts';

import { createAccount, deleteAccount, getAccounts, updateAccount } from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, EmptyState, ErrorNote, Field, Loading, Modal, Money, Note, Progress, SubmitButton } from '../components/ui';

/** Las clases de cuenta, con su nombre en cristiano. */
const ACCOUNT_KINDS: ReadonlyArray<{ value: string; label: string; help: string; icon: typeof Wallet }> = [
  { value: 'CASH', label: 'Efectivo', help: 'Lo que llevas en el bolsillo o en casa.', icon: Banknote },
  { value: 'SAVINGS', label: 'Cuenta de ahorros', help: 'Tu cuenta del banco.', icon: PiggyBank },
  { value: 'CHECKING', label: 'Cuenta corriente', help: 'Cuenta corriente del banco.', icon: Landmark },
  { value: 'CREDIT_CARD', label: 'Tarjeta de crédito', help: 'Cupo que vas usando. Es una deuda, no dinero tuyo.', icon: CreditCard },
  { value: 'DIGITAL_WALLET', label: 'Billetera digital', help: 'Nequi, Daviplata, PayPal…', icon: Wallet },
  { value: 'OTHER', label: 'Otra', help: 'Cualquier otra cosa.', icon: Wallet },
];

const KIND_LABEL = new Map(ACCOUNT_KINDS.map((k) => [k.value, k.label]));

export function AccountsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const accounts = useQuery({
    queryKey: ['accounts', showArchived],
    queryFn: () => getAccounts(showArchived),
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['summary'] }),
      queryClient.invalidateQueries({ queryKey: ['reports'] }),
    ]);
  };

  const remove = useMutation({
    mutationFn: deleteAccount,
    onSuccess: refresh,
  });

  const removeError = remove.error instanceof ApiError ? remove.error : null;

  const list = accounts.data?.accounts ?? [];
  const assets = list.filter((a) => a.kind !== 'CREDIT_CARD');
  const cards = list.filter((a) => a.kind === 'CREDIT_CARD');
  const totalAssets = assets.reduce((acc, a) => acc + a.balance, 0);

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Cuentas</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">Dónde tienes el dinero.</p>
        </div>
        <button
          type="button"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          onClick={() => setCreating(true)}
        >
          <Plus size={18} aria-hidden="true" />
          Cuenta
        </button>
      </header>

      {accounts.isLoading && <Loading label="Calculando tus saldos…" />}
      {removeError && <ErrorNote message={removeError.message} />}

      {accounts.data && list.length === 0 && (
        <Card>
          <EmptyState
            title="Sin cuentas"
            message="Añade dónde tienes el dinero: el efectivo, tu cuenta del banco, la tarjeta. Los saldos se calculan solos con los movimientos."
            action={
              <button
                type="button"
                className="ed-btn-primary !w-auto !px-4"
                onClick={() => setCreating(true)}
              >
                Añadir la primera
              </button>
            }
          />
        </Card>
      )}

      {assets.length > 0 && (
        <>
          <Card>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-ed-text-muted">Tienes en total</span>
              <Money amount={totalAssets} className="font-display text-xl font-bold text-ed-gold" />
            </div>
          </Card>

          <Card title="Tu dinero">
            <ul className="divide-y divide-ed-bg-border/25">
              {assets.map((account) => {
                const kind = ACCOUNT_KINDS.find((k) => k.value === account.kind);
                const Icon = kind?.icon ?? Wallet;

                return (
                  <li key={account.id} className="flex items-center gap-3 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ed-bg-elevated text-ed-text-muted">
                      <Icon size={17} aria-hidden="true" />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{account.name}</span>
                      <span className="block text-xs text-ed-text-muted">
                        {KIND_LABEL.get(account.kind) ?? account.kind}
                        {account.isArchived && ' · archivada'}
                      </span>
                    </span>

                    <span className="flex shrink-0 flex-col items-end gap-0.5">
                      <Money
                        amount={account.balance}
                        className={`text-sm font-semibold ${
                          account.balance < 0 ? 'text-ed-coral-light' : ''
                        }`}
                      />
                      <span className="flex gap-1.5">
                        <button
                          type="button"
                          className="text-xs text-ed-cyan-light hover:underline"
                          onClick={() => setEditing(account)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="text-xs text-ed-text-muted hover:text-ed-coral-light"
                          onClick={() => {
                            if (
                              window.confirm(
                                `¿Eliminar «${account.name}»? Si tiene movimientos no se borrará: se archivará para que el histórico siga cuadrando.`,
                              )
                            ) {
                              remove.mutate(account.id);
                            }
                          }}
                        >
                          Eliminar
                        </button>
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </>
      )}

      {/* Tarjetas de crédito, aparte y con su cupo. */}
      {cards.length > 0 && (
        <Card title="Tarjetas de crédito">
          <ul className="space-y-4">
            {cards.map((card) => {
              const owed = Math.max(0, -card.balance);
              const limit = card.creditLimit ?? 0;
              const usage = limit > 0 ? (owed / limit) * 100 : 0;

              return (
                <li key={card.id}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-medium">
                        <CreditCard size={15} className="shrink-0 text-ed-text-muted" aria-hidden="true" />
                        <span className="truncate">{card.name}</span>
                      </p>
                      <p className="mt-0.5 text-xs text-ed-text-muted">
                        {limit > 0 ? (
                          <>
                            Cupo {formatCOP(limit)} · disponible {formatCOP(Math.max(0, limit - owed))}
                          </>
                        ) : (
                          'Sin cupo registrado'
                        )}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <Money amount={owed} className="block font-semibold text-ed-coral-light" />
                      <span className="block text-xs text-ed-text-muted">debes</span>
                    </div>
                  </div>

                  {limit > 0 && (
                    <div className="mt-2">
                      <Progress
                        percent={usage}
                        tone={usage > 70 ? 'coral' : usage > 40 ? 'gold' : 'success'}
                        label={`${card.name}: ${Math.round(usage)} por ciento del cupo usado`}
                      />
                      {usage > 70 && (
                        <p className="mt-1 text-xs text-ed-coral-light">
                          Estás usando más del 70 % del cupo. Además de deber mucho, eso te sube el
                          riesgo ante el banco.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className="text-xs text-ed-cyan-light hover:underline"
                      onClick={() => setEditing(card)}
                    >
                      Editar
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <label className="flex items-center gap-2 px-1 text-sm text-ed-text-muted">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-ed-bg-border"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        Ver también las archivadas
      </label>

      <Note>
        El saldo de cada cuenta <strong>se calcula</strong> con sus movimientos, no se guarda ni se
        ajusta a mano. Por eso borrar un gasto devuelve el dinero solo, y por eso este número nunca
        se desincroniza.
      </Note>

      {(creating || editing) && (
        <AccountModal
          account={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/** Crear o editar una cuenta. */
function AccountModal({
  account,
  onClose,
  onSaved,
}: {
  account: Account | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [name, setName] = useState(account?.name ?? '');
  const [kind, setKind] = useState(account?.kind ?? 'CASH');
  const [initialText, setInitialText] = useState(
    account ? String(Math.abs(account.initialBalance)) : '',
  );
  const [limitText, setLimitText] = useState(
    account?.creditLimit !== null && account?.creditLimit !== undefined
      ? String(account.creditLimit)
      : '',
  );

  const isCard = kind === 'CREDIT_CARD';
  const isEditing = account !== null;

  const save = useMutation({
    mutationFn: () => {
      const initialBalance = parseAmountFromText(initialText) ?? 0;

      if (isEditing && account) {
        return updateAccount(account.id, {
          name: name.trim(),
          creditLimit: isCard ? (parseAmountFromText(limitText) ?? undefined) : undefined,
        });
      }

      return createAccount({
        name: name.trim(),
        kind: kind as 'CASH',
        initialBalance: isCard ? 0 : initialBalance,
        creditLimit: isCard ? (parseAmountFromText(limitText) ?? undefined) : undefined,
      });
    },
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? 'Editar cuenta' : 'Nueva cuenta'}
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={name.trim().length === 0}
          onClick={() => save.mutate()}
        >
          {isEditing ? 'Guardar cambios' : 'Crear cuenta'}
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field label="Nombre" htmlFor="nombre-cuenta">
          <input
            id="nombre-cuenta"
            className="ed-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bancolombia, Efectivo, Nequi…"
            autoFocus
            autoComplete="off"
          />
        </Field>

        {!isEditing && (
          <>
            <Field label="Tipo" htmlFor="tipo-cuenta">
              <select
                id="tipo-cuenta"
                className="ed-input"
                value={kind}
                onChange={(e) => setKind(e.target.value as 'CASH')}
              >
                {ACCOUNT_KINDS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <p className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 px-3 py-2 text-xs text-ed-text-silver">
              {ACCOUNT_KINDS.find((k) => k.value === kind)?.help}
            </p>
          </>
        )}

        {isCard ? (
          <Field
            label="Cupo de la tarjeta"
            htmlFor="cupo-cuenta"
            hint="Lo que te presta el banco. Nos sirve para avisarte cuando estés cerca del límite."
          >
            <input
              id="cupo-cuenta"
              className="ed-input ed-money"
              inputMode="numeric"
              value={limitText}
              onChange={(e) => setLimitText(e.target.value)}
              placeholder="3.000.000"
              autoComplete="off"
            />
          </Field>
        ) : (
          !isEditing && (
            <Field
              label="¿Cuánto tienes hoy ahí?"
              htmlFor="saldo-inicial"
              hint="Solo el punto de partida. A partir de aquí, el saldo lo calculan los movimientos."
            >
              <input
                id="saldo-inicial"
                className="ed-input ed-money"
                inputMode="numeric"
                value={initialText}
                onChange={(e) => setInitialText(e.target.value)}
                placeholder="500.000"
                autoComplete="off"
              />
            </Field>
          )
        )}

        {isCard && (
          <Note>
            Una tarjeta de crédito es una <strong>deuda</strong>, no dinero tuyo. Su saldo empieza en
            cero y va bajando a medida que gastas: lo que quede en negativo es lo que debes.
          </Note>
        )}

        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}
